'use client'

import { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Instrument_Serif, DM_Sans, Poppins } from 'next/font/google'
import { Star, Home } from 'lucide-react'
import { trackPostHogEvent } from '@/lib/utils/dataLayer'
import caseStudies from '@/data/case-studies.json'

const instrumentSerif = Instrument_Serif({
  subsets: ['latin'],
  weight: ['400'],
  style: ['italic'],
})

const dmSans = DM_Sans({
  subsets: ['latin'],
  weight: ['400', '500', '700'],
})

const poppins = Poppins({
  subsets: ['latin'],
  weight: ['300', '400'],
  variable: '--hero-sub-font',
})

type CardVariant = 'peach' | 'lined' | 'mint' | 'butter'

type CaseCard = {
  id: string
  variant: CardVariant
  logo: { src: string; alt: string }
  url: string
  category: string
  stat: string
  statLabel: string
  footer: string
  wave: string
  peak: string
}

type CaseStudy = {
  url: string
  category: string
  stat: string
  statLabel: string
  footer: string
  logo: { src: string; alt: string }
}

const VARIANTS: CardVariant[] = ['peach', 'lined', 'mint', 'butter']

const WAVES = [
  'M0,45 Q50,35 100,40 Q150,15 200,20 Q240,25 280,30',
  'M0,40 Q70,20 140,30 T280,25',
  'M0,50 Q70,30 140,35 Q210,20 280,25',
  'M0,35 Q70,25 140,20 Q210,30 280,22',
] as const

const PEAKS = ['50%', '65%', '75%', '42%'] as const

const CASES = caseStudies as CaseStudy[]

function buildCards(cases: CaseStudy[]): CaseCard[] {
  return cases.map((item, i) => ({
    id: `case-${i}-${item.url.replace(/\W/g, '')}`,
    variant: VARIANTS[i % VARIANTS.length],
    logo: item.logo,
    url: item.url,
    category: item.category,
    stat: item.stat,
    statLabel: item.statLabel,
    footer: item.footer,
    wave: WAVES[i % WAVES.length],
    peak: PEAKS[i % PEAKS.length],
  }))
}

const SERVICE_TAGS = [
  'Ads',
  'AI SEO',
  'CRO',
  'Web Builds',
  'CRM',
  'Analytics',
  'Workflows',
  'AI Widgets',
] as const

const TRUST_AVATARS = [
  '/images/clients/eileen.jpg',
  '/images/clients/goodvets.png',
  '/images/clients/spinning.png',
  '/images/clients/cerebrocapital.jpeg',
] as const

const STACK_PEEK_DESKTOP = 5
const STACK_PEEK_MOBILE = 2
const MOBILE_MQ = '(max-width: 56.99rem)'

function useStackPeek() {
  const [peek, setPeek] = useState(STACK_PEEK_MOBILE)
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ)
    const sync = () => setPeek(mq.matches ? STACK_PEEK_MOBILE : STACK_PEEK_DESKTOP)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])
  return peek
}

function ScoopCard({ card, active }: { card: CaseCard; active: boolean }) {
  return (
    <article className={`case-scoop case-scoop--${card.variant}${active ? ' is-front' : ''} ${dmSans.className}`}>
      <span className={`case-scoop-badge${active ? ' is-active' : ''}`}>
        <svg
          className="case-scoop-badge-svg"
          viewBox="0 0 24 24"
          width="96"
          height="96"
          aria-hidden
        >
          <defs>
            <clipPath id={`case-logo-clip-${card.id}`}>
              <circle cx="12" cy="12" r="8" />
            </clipPath>
          </defs>
          <image
            href={card.logo.src}
            width="24"
            height="24"
            preserveAspectRatio="xMidYMid slice"
            clipPath={`url(#case-logo-clip-${card.id})`}
          />
        </svg>
      </span>
      <div className="case-scoop-inner">
        <div className="case-scoop-glow" aria-hidden />
        <div className="case-scoop-head">
          <div className="case-scoop-card-title">{card.url}</div>
          <div className="case-scoop-card-cat">{card.category}</div>
        </div>
        <div className="case-scoop-metric">
          <div className="case-scoop-metric-value">
            {card.stat}
            <span className="case-scoop-arrow">↗</span>
          </div>
          <div className="case-scoop-metric-label">{card.statLabel}</div>
        </div>
        <div className="case-scoop-chart">
          <div className="case-scoop-wave">
            <svg width="280" height="60" viewBox="0 0 280 60" aria-hidden>
              <path className="case-scoop-wave-path" d={card.wave} />
            </svg>
          </div>
         
        </div>
        <p className="case-scoop-blurb">
          {card.footer}
        </p>
      </div>
    </article>
  )
}

type TrustLogo = { name: string; src: string }
type HeroPath = { label: string; href: string }

type CaseStudyCardsProps = {
  ctaHref?: string
  bgSrc?: string
  bgOverlay?: boolean
  badgeLabel?: string
  badgeIcon?: 'star' | 'home'
  cases?: CaseStudy[]
  heading?: string
  headingEm?: string
  sub?: string
  ctaLabel?: string
  trustLogos?: TrustLogo[]
  trustLabel?: string
  trustHref?: string
  tags?: readonly string[]
  paths?: HeroPath[]
  headingInline?: boolean
  showTrust?: boolean
  badgePreserveCase?: boolean
  bgPosition?: string
}

export default function CaseStudyCards({
  ctaHref = '#services',
  bgSrc = '/images/homepage/new-hero.avif',
  bgOverlay = false,
  badgeLabel = 'Top Service Provider',
  badgeIcon = 'star',
  cases = CASES,
  heading = 'The services behind',
  headingEm = 'these numbers.',
  sub = 'Hire the full stack in one place — audit, build, or automate. No handoffs.',
  ctaLabel = 'Compare Services',
  trustLogos,
  trustLabel = 'View client reviews',
  trustHref,
  tags = SERVICE_TAGS,
  paths,
  headingInline = false,
  showTrust = true,
  badgePreserveCase = false,
  bgPosition,
}: CaseStudyCardsProps) {
  const tagSplit = Math.ceil(tags.length / 2)
  const CARDS = useMemo(() => buildCards(cases), [cases])
  const [active, setActive] = useState(0)
  const [hasCycled, setHasCycled] = useState(false)
  const stackPeek = useStackPeek()

  const goNext = () => {
    setHasCycled(true)
    setActive((i) => (i + 1) % CARDS.length)
  }

  return (
    <section id="growth-club" className="case-scoop-section case-scoop-section--hero" aria-label="Marketing services">
      <div className={`case-scoop-hero-bg${bgOverlay ? ' case-scoop-hero-bg--overlay' : ''}`} aria-hidden>
        <Image
          src={bgSrc}
          alt=""
          fill
          className={`object-cover ${bgPosition ? '' : 'object-center'}`}
          style={bgPosition ? { objectPosition: bgPosition } : undefined}
          priority
          sizes="100vw"
        />
      </div>
      <header className={`case-scoop-title ${dmSans.className}`}>
        {tags.length > 0 ? (
        <ul className="case-scoop-title-tags case-scoop-title-tags--left" aria-hidden>
          {tags.slice(0, tagSplit).map((tag) => (
            <li key={tag} className="case-scoop-title-tag">
              {tag}
            </li>
          ))}
        </ul>
        ) : <span />}
        {badgeLabel ? (
        <p className={`case-scoop-title-pill${badgePreserveCase ? ' case-scoop-title-pill--preserve-case' : ''}`}>
          <span>
            {badgeIcon === 'home' ? (
              <Home fill="currentColor" strokeWidth={0} aria-hidden />
            ) : (
              <Star fill="currentColor" strokeWidth={0} aria-hidden />
            )}
            {badgeLabel}
          </span>
        </p>
        ) : <span />}
        {tags.length > 0 ? (
        <ul className="case-scoop-title-tags case-scoop-title-tags--right" aria-hidden>
          {tags.slice(tagSplit).map((tag) => (
            <li key={tag} className="case-scoop-title-tag">
              {tag}
            </li>
          ))}
        </ul>
        ) : <span />}
      </header>
      <div className="case-scoop-layout">
        <div className={`case-scoop-copy ${dmSans.className}`}>
          <h1 className={`case-scoop-heading${headingInline ? ' case-scoop-heading--inline' : ''}`}>
            {heading}
            {headingEm ? (
              <>
                {' '}
                <span className={`case-scoop-heading-em ${instrumentSerif.className}`}>{headingEm}</span>
              </>
            ) : null}
          </h1>
          <p className={`case-scoop-sub ${poppins.className} ${poppins.variable}`}>
            {sub}
          </p>

          <div className="case-scoop-cta">
            <Link
              href={ctaHref}
              className={`hero-quote-cta ${dmSans.className}`}
              onClick={(event) => {
                trackPostHogEvent('cta_click', {
                  cta_text: ctaLabel,
                  cta_location: 'homepage_hero',
                  cta_type: 'primary',
                })
                if (!ctaHref.startsWith('#')) return
                event.preventDefault()
                document.getElementById(ctaHref.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }}
            >
              <span className="hero-quote-cta__text">{ctaLabel}</span>
              <span className="hero-quote-cta__shimmer" aria-hidden="true" />
            </Link>
          </div>

          {paths?.length ? (
            <ul className="case-scoop-paths" aria-label="Ways to work with us">
              {paths.map((path) => (
                <li key={path.href}>
                  <a href={path.href}>{path.label}</a>
                </li>
              ))}
            </ul>
          ) : null}

          {showTrust ? (
          <div className={`case-scoop-trust${trustLogos?.length ? ' case-scoop-trust--logos' : ''}`}>
            {trustLogos?.length ? (
              <>
                <p>{trustLabel}</p>
                <div className="case-scoop-trust-logos">
                  {trustLogos.map(logo => (
                    <div key={logo.name} title={logo.name}>
                      <img src={logo.src} alt={logo.name} />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="case-scoop-trust-faces">
                  {TRUST_AVATARS.map((src) => (
                    <img key={src} src={src} alt="" />
                  ))}
                </div>
                <div>
                  <div className="case-scoop-trust-stars" aria-hidden>
                    {Array.from({ length: 5 }, (_, i) => (
                      <svg key={i} viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  {trustHref ? (
                    <a
                      href={trustHref}
                      className="case-scoop-trust-link"
                      onClick={(event) => {
                        if (!trustHref.startsWith('#')) return
                        event.preventDefault()
                        document.getElementById(trustHref.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                      }}
                    >
                      {trustLabel}
                    </a>
                  ) : (
                    <p>{trustLabel}</p>
                  )}
                </div>
              </>
            )}
          </div>
          ) : null}
        </div>

        <div className="case-scoop-right">
          <div className="case-scoop-deck">
            {CARDS.map((card, index) => {
              const stackPos = (index - active + CARDS.length) % CARDS.length
              const outgoing = hasCycled && stackPos === CARDS.length - 1
              if (stackPos >= stackPeek && !outgoing) return null
              return (
                <div
                  key={card.id}
                  className="case-scoop-layer"
                  style={{
                    zIndex: outgoing ? CARDS.length + 1 : CARDS.length - stackPos,
                    opacity: outgoing ? 0 : 1,
                    pointerEvents: stackPos === 0 ? 'auto' : 'none',
                    transform: outgoing
                      ? 'translate(-48%, 0)'
                      : `translate(calc(var(--stack-x) * ${stackPos}), 0)`,
                  }}
                  aria-hidden={stackPos !== 0}
                >
                  <ScoopCard card={card} active={stackPos === 0} />
                </div>
              )
            })}
          </div>

          <button
            type="button"
            className="case-scoop-nav"
            aria-label="Next case study"
            onClick={goNext}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M9 5l7 7-7 7"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  )
}
